import { PostSaleLineItems, EtrUploadedDocumentType } from '../types/postSale';
import TradeListPaymentsOnHold from './TradeListPaymentsOnHold';

export interface ITradeListPaymentsOnHold {
  id: number;
  make: string;
  model: string;
  trim: string;
  thumbnailUrl: string;
  year: number;
  vin: string;
  topBid: number;
  bidPlacedTime: string;
  onHoldStatuses: object[];
  isEtrTrade?: boolean;
}

interface ITradeListPaymentsOnHoldOptions {
  filterId: number;
  sortId: number;
  etrOffset: number;
  tradesOnPage: number;
}

enum FilterId {
  NEEDS_LIEN_RELEASE = 1,
  NEEDS_OWNERSHIP = 2
}

enum SortId {
  OLDEST = 0,
  NEWEST = 1,
  HIGHEST = 2
}

enum DocumentOnHoldStatus {
  OWING = 0,
  PROCESSING = 1
}

const ADESA_ADDRESS = 'ADESA Vancouver\\n7111 No. 8 Rd\\nRichmond, BC V6W 1L9';
const OPENLANE_ADDRESS =
  'OPENLANE - Suite 600\\n370 King Street W\\nToronto, Ontario, M5V 1J9';
const EMAIL_ADDRESS = 'liens@openlane.com';

const filterEtrTrades = (
  etrTrades: ITradeListPaymentsOnHold[],
  filterOption: number
) => {
  const options = {
    [FilterId.NEEDS_LIEN_RELEASE]: 'needsLienRelease',
    [FilterId.NEEDS_OWNERSHIP]: 'needsOwnership'
  };

  if (!filterOption) {
    return etrTrades;
  }
  const filteredEtrTrades = etrTrades?.filter(
    etrTrade => etrTrade[options[filterOption]]
  );

  return filteredEtrTrades;
};

const sortTrades = (trades: ITradeListPaymentsOnHold[], sortOption: number) => {
  const sortOptions = {
    [SortId.OLDEST]: (a, b) =>
      new Date(a.bidPlacedTime).getTime() - new Date(b.bidPlacedTime).getTime(),
    [SortId.NEWEST]: (a, b) =>
      new Date(b.bidPlacedTime).getTime() - new Date(a.bidPlacedTime).getTime(),
    [SortId.HIGHEST]: (a, b) => b.topBid - a.topBid
  };

  return trades.sort(sortOptions[sortOption]);
};

const getOwnershipOnHoldStatus = (needsOwnership, uploadedDocuments) => {
  const previouslyUploadedTitle = uploadedDocuments?.some(
    uploadedDocument =>
      uploadedDocument.documentType === EtrUploadedDocumentType.TITLE
  );

  if (needsOwnership && previouslyUploadedTitle) {
    return DocumentOnHoldStatus.PROCESSING;
  }

  if (needsOwnership) {
    return DocumentOnHoldStatus.OWING;
  }

  return null;
};

class MergedTradeListPaymentsOnHold {
  tradeSummaries: ITradeListPaymentsOnHold[];

  legacyDisplayedCount: number;

  etrDisplayedCount: number;

  constructor(
    data: any = [],
    etrData: any = [],
    options: ITradeListPaymentsOnHoldOptions
  ) {
    const { tradeSummaries } = new TradeListPaymentsOnHold(data);
    const mappedEtrData = etrData
      ? MergedTradeListPaymentsOnHold.mapEtrTrades(
          filterEtrTrades(etrData, options?.filterId)
        )
      : [];
    const sortedEtrTrades = sortTrades(mappedEtrData, options?.sortId).slice(
      options?.etrOffset
    );
    const mergedTrades = sortTrades(
      tradeSummaries.concat(sortedEtrTrades),
      options?.sortId
    );

    this.tradeSummaries = mergedTrades?.slice(0, options?.tradesOnPage);
  }

  private static mapEtrTrades(trades: any = []): ITradeListPaymentsOnHold[] {
    return trades.map(trade => ({
      id: trade.stockId,
      isEtrTrade: true,
      make: trade.make,
      model: trade.model,
      thumbnailUrl: trade.vehicleImageUrl,
      year: trade.year,
      vin: trade.vin,
      topBid: trade.salePrice && trade.salePrice / 100,
      bidPlacedTime: trade.dateLanded,
      purchaseId: trade.purchaseId,
      dealerProvince: trade.province,
      canUploadDigitalTitle: trade.canUploadTitle,
      uploadedDocuments: trade.uploadedDocuments,
      onHoldStatuses: [
        {
          value: trade.province === 'BC' ? ADESA_ADDRESS : OPENLANE_ADDRESS,
          key: PostSaleLineItems.OWNERSHIP,
          status: getOwnershipOnHoldStatus(
            trade.needsOwnership,
            trade.uploadedDocuments
          )
        },
        {
          value: EMAIL_ADDRESS,
          isEmail: true,
          key: PostSaleLineItems.LIEN_RELEASE,
          status: trade.needsLienRelease ? DocumentOnHoldStatus.OWING : null
        }
      ]
    }));
  }
}

export default MergedTradeListPaymentsOnHold;
